import axios from "axios";
import { useContext, useMemo, useState } from "react";
import moment from "moment";
import AuthContext from "../../../contexts/AuthContext";
import showNotification from "../../../utils/showNotification";
import { ERROR_HANDLER } from "../../../utils/error-handler";

const URL = `${process.env.REACT_APP_BACKEND_SERVER}admin/marketing/community/teachers-of-month`;

export default function useTeacherCommunity() {
    const { authToken } = useContext(AuthContext);

    const [isLoading, setIsLoading] = useState(false);
    const [result, setResult] = useState(null);
    const [message, setMessage] = useState("");
    const [wasEdited, setWasEdited] = useState(false);
    const [copied, setCopied] = useState(false);

    // Arranca en el mes actual. Si lo corrés el día 1 seguramente quieras el anterior.
    const [period, setPeriod] = useState({
        year: moment().year(),
        month: moment().month() + 1,
    });

    const axiosConfig = {
        headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + authToken,
        },
    };

    /** Últimos 12 meses para el selector. */
    const monthOptions = useMemo(() => {
        return Array.from({ length: 12 }, (_, i) => {
            const m = moment().subtract(i, "months");
            return {
                value: `${m.year()}-${m.month() + 1}`,
                label: m.format("MMMM YYYY"),
                year: m.year(),
                month: m.month() + 1,
            };
        });
    }, []);

    const selectedPeriodValue = `${period.year}-${period.month}`;

    const changePeriod = (value) => {
        const [year, month] = value.split("-").map(Number);
        setPeriod({ year, month });
        // El resultado anterior corresponde a otro mes: lo limpiamos.
        setResult(null);
        setMessage("");
        setWasEdited(false);
    };

    const generate = async () => {
        setIsLoading(true);
        setCopied(false);
        try {
            const { data } = await axios.get(URL, {
                ...axiosConfig,
                params: { year: period.year, month: period.month },
            });

            setResult(data);
            setMessage(data.message || "");
            setWasEdited(false);

            if (!data.ranked?.length) {
                showNotification({
                    color: "yellow",
                    title: "Sin docentes destacados",
                    message: "Ningún docente alcanzó el mínimo de clases y valoraciones este mes.",
                    autoClose: 8000,
                });
            }
        } catch (error) {
            const message_error = error?.response?.data?.message;
            showNotification({
                color: "red",
                status: "error",
                title: "Error al generar el resumen",
                message: `Detalle: ${message_error || ERROR_HANDLER[error?.response?.status] || ERROR_HANDLER.defaultError}`,
                autoClose: 12000,
            });
        } finally {
            setIsLoading(false);
        }
    };

    const editMessage = (value) => {
        setMessage(value);
        setWasEdited(true);
        setCopied(false);
    };

    const restoreMessage = () => {
        setMessage(result?.message || "");
        setWasEdited(false);
        setCopied(false);
    };

    /**
     * navigator.clipboard sólo existe en contextos seguros (https o localhost).
     * En un admin servido por http plano no está, de ahí el fallback.
     */
    const copyMessage = async (text = message) => {
        if (!text) return;

        const legacyCopy = () => {
            const ta = document.createElement("textarea");
            ta.value = text;
            ta.style.position = "fixed";
            ta.style.opacity = "0";
            document.body.appendChild(ta);
            ta.select();
            const ok = document.execCommand("copy");
            document.body.removeChild(ta);
            return ok;
        };

        try {
            if (navigator.clipboard && window.isSecureContext) {
                await navigator.clipboard.writeText(text);
            } else if (!legacyCopy()) {
                throw new Error("execCommand falló");
            }

            setCopied(true);
            setTimeout(() => setCopied(false), 2500);
            showNotification({
                color: "green",
                title: "Copiado",
                message: "El mensaje está listo para pegar en WhatsApp.",
                autoClose: 4000,
            });
        } catch (error) {
            showNotification({
                color: "red",
                title: "No se pudo copiar",
                message: "Seleccioná el texto del cuadro y copiálo a mano.",
                autoClose: 8000,
            });
        }
    };

    const hasWarnings =
        !!result?.warnings?.unmatchedReviewNames?.length ||
        !!result?.warnings?.countriesWithoutData ||
        !!result?.excluded?.length;

    return {
        isLoading,
        result,
        message,
        wasEdited,
        copied,
        period,
        selectedPeriodValue,
        monthOptions,
        changePeriod,
        generate,
        editMessage,
        restoreMessage,
        copyMessage,
        hasWarnings,
    };
}